<template>
  <div id="pre-order-page" v-cloak>
    <VBlock class="pre-order">
      <Loading :isShow="isLoading" />
      <VBlock class="page-wrapper" :direction="Direction.column" :sort="Sort.left_center">
        <PreOrderLoginContainer />
        <PreOrderDeliveryContainer class="element-distance" />
        <PreOrderDeliveryMemoContainer class="element-distance" />
        <PreOrderPaymentContainer
          :isLoading="isLoading"
          :isUnsignChangeSign="isUnsignChangeSign"
          :onClickNativeBack="onClickNativeBack"
        />
        <PreOrderTermsContainer />
        <PreOrderPaymentButtonContainer />
      </VBlock>
    </VBlock>
  </div>
</template>

<script>
import VBlock, { Direction, Sort } from '@/presentationals/v2/components/layouts/molecules/VBlock'

import Loading from '@/presentationals/v2/components/containers/LoadingContainer'
import PreOrderPaymentButtonContainer from '@/presentationals/v2/components/containers/PreOrderPaymentButtonContainer'
import PreOrderTermsContainer from '@/presentationals/v2/components/containers/PreOrderTermsContainer'
import PreOrderPaymentContainer from '@/presentationals/v2/components/containers/PreOrderPaymentContainer'
import PreOrderDeliveryMemoContainer from '@/presentationals/v2/components/containers/PreOrderDeliveryMemoContainer'
import PreOrderDeliveryContainer from '@/presentationals/v2/components/containers/PreOrderDeliveryContainer'
import PreOrderLoginContainer from '@/presentationals/v2/components/containers/PreOrderLoginContainer'
import { GlobalFunctionName, setGlobalFunction } from '@/utils/nativeBridge'
import { Action } from '@/plugins/AppManager'
import { NativeStorageKey } from '@/plugins/NativeStorageManager'
import { LogScreenName, LogType, LogGroup, LogEvent } from '@/plugins/AppLogManager'

import { isProvideBaeminPoint } from '../../../../config/featureProvider'
import { isLocalhost } from '../../../../utils/dev'

export default {
  name: 'PreOrderV2Page',
  components: {
    PreOrderLoginContainer,
    PreOrderDeliveryContainer,
    PreOrderDeliveryMemoContainer,
    PreOrderPaymentContainer,
    PreOrderTermsContainer,
    PreOrderPaymentButtonContainer,
    VBlock,
    Loading
  },
  data() {
    return {
      Direction,
      Sort,
      isLoading: true,
      isUnsignChangeSign: false
    }
  },
  methods: {
    async loadByLocalStorage() {
      const baeminShopNo = await this.$nativeStorageManager.get(NativeStorageKey.BAEMIN_SHOP_NO)
      const accessToken = await this.$nativeStorageManager.get(NativeStorageKey.ACCESS_TOKEN)
      const key = await this.$nativeStorageManager.get(NativeStorageKey.KEY)
      const sessionId = await this.$nativeStorageManager.get(NativeStorageKey.SESSION_ID)

      this.$store.commit('setBaeminShopNo', baeminShopNo)
      this.$store.commit('setAccessToken', accessToken)
      this.$store.commit('setKey', key)
      this.$store.commit('setSessionId', sessionId)

      await this.$store.dispatch('loadMemberByKey')
    },

    onClickNativeBack() {
      this.$hideVirtualKeyboard()

      this.$store.dispatch('modal/open', {
        type: 'Confirm',
        message: '다음에 다시 주문하시겠습니까?',
        onOK: async () => {
          this.$appManager.action(Action.CLOSE)
        },
        onCancel: async () => {
          this.$store.dispatch('modal/close')
        }
      })
    },

    // TODO check src/views/order/PreOrderV2.vue:98
    onInjectTypedData(json) {
      const { type, data } = json

      if (!this._.isString(type)) return false

      if (type === 'fetchAddressDetail') this.pullDeliveryAddress(data)
      if (type === 'fetchAppsFlyerId') this.pullAppsFlyerId(data)
      if (type === 'refreshUser') this.pullUserAuthorization(data)

      return true
    },

    pullDeliveryAddress(data) {
      this.$store.commit('preOrder/setDelivery', {
        roadAddress: data.roadAddress,
        detailAddress: data.detailAddress
      })
    },

    async pullAppsFlyerId(data) {
      const { afid, adid } = data
      this.$store.commit('preOrder/setAppsFlyerIds', { afid, adid })
    },

    async pullUserAuthorization(data) {
      const { userBaedal: key, accessToken } = data

      this.$store.commit('setKey', key)

      if (this._.isString(accessToken)) {
        this.$store.commit('setAccessToken', accessToken.split(' ').pop())
      }

      this.$store.commit('setSignedUser', true)

      await this.$store.dispatch('loadMemberByKey')

      const methodOptionsData = await this.$store.dispatch(
        'preOrder/loadPreOrderPaymentMethodOptions'
      )

      this.$store.commit('setPaymentMethodOptions', {
        primaryMethodTypes: methodOptionsData.result.primaryMethods,
        subMethodTypes: methodOptionsData.result.subMethods
      })

      await this.loadOptional()

      const resultSets = await this.loadEssential()

      this.isUnsignChangeSign = true
      // this.updatePaymentMethods = {
      //   primaryMethodTypes: methodOptionsData.result.primaryMethods,
      //   subMethodTypes: methodOptionsData.result.subMethods
      // }

      if (resultSets.some(e => e.failed)) {
        this.$store.dispatch('modal/open', {
          type: 'Check',
          message: '주문 중 문제가 발생했습니다. 잠시 후 다시 시도해주세요.',
          onOK: async () => {
            this.$appManager.action(Action.CLOSE)
          }
        })
      }
    },

    async loadOptional() {
      this.$store.dispatch('preOrder/loadDeliveryReservationOptions')
      this.$store.dispatch('preOrder/loadCoupons')
      this.$store.dispatch('preOrder/loadGiftCard')
      this.$store.dispatch('preOrder/loadBaeminPoint')
      this.$store.dispatch('preOrder/loadPaymentNotice')
    },

    async loadEssential() {
      return Promise.all([
        this.$store.dispatch('preOrder/loadPreOrderOrderer'),
        this.$store.dispatch('preOrder/loadPreOrderRecentDelivery'),
        this.$store.dispatch('preOrder/loadPreOrderFirstOrder'),
        this.$store.dispatch('preOrder/loadPreOrderRecentPaymentMethod')
      ])
    },

    preOrderViewLog() {
      const params = {
        ShopNo: this.$store.getters.baeminShopNo || ''
      }

      // M2
      this.$appLogManager.post({
        screenName: LogScreenName.MARKET_ORD,
        type: LogType.VIEW,
        params
      })
    }
  },

  async created() {
    // TODO check src/views/order/PreOrderV2.vue:91
    setGlobalFunction(GlobalFunctionName.ON_INJECT_TYPED_DATA, this.onInjectTypedData)
    setGlobalFunction(GlobalFunctionName.ON_CLICK_NATIVE_BACK, this.onClickNativeBack)
    await this.loadByLocalStorage()

    const { result, failed } = await this.$store.dispatch('preOrder/loadPreOrderSheet', {
      orderSheetKey: this.$route.params.key
    })

    if (failed) {
      this.$store.dispatch('modal/open', {
        type: 'Check',
        message: '주문 중 문제가 발생했습니다. 잠시 후 다시 시도해주세요.',
        onOK: async () => {
          this.$appManager.action(Action.CLOSE)
        }
      })

      this.isLoading = false
      return
    }

    this.$store.dispatch('preOrder/loadPreOrderTerms')

    this.loadOptional()

    const resultSets = await this.loadEssential()

    if (resultSets.some(e => e.failed)) {
      this.$store.dispatch('modal/open', {
        type: 'Check',
        message: '주문 중 문제가 발생했습니다. 잠시 후 다시 시도해주세요.',
        onOK: async () => {
          this.$appManager.action(Action.CLOSE)
        }
      })

      this.isLoading = false
      return
    }

    await this.$store.dispatch('preOrder/loadPreOrderRecentCashReceipt')

    const {
      baeminMemberId: memberId,
      baeminMemberNo: memberNo,
      sessionId,
      deviceId,
      appVersion
    } = this.$store.getters

    this.$appLogManager.init({
      memberId,
      memberNo,
      sessionId,
      deviceId,
      appVersion
    })

    this.preOrderViewLog()

    this.isLoading = false

    // this.pullUserAuthorization(this.$store.getters)
    // TODO implement src/views/order/PreOrderV2.vue:264
    // this.$modal.show('payment-method-modal')
    // this.$modal.hide('payment-method-modal')
  }
}
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/modules/colors';

#pre-order-page {
  background-color: $background-gray;
  /* overflow: hidden; */

  .pre-order {
    .page-wrapper {
      padding-top: 10px;
      padding-bottom: constant(safe-area-inset-bottom);
      padding-bottom: env(safe-area-inset-bottom);

      .element-distance {
        margin-bottom: 10px;
      }
    }
  }
}
</style>
